import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import {
  House, ShoppingCart, Package, Users, Truck, Wallet,
  Receipt, UserGear, SignOut, Lightning, Sun, Moon, Wrench, ArrowsLeftRight
} from "@phosphor-icons/react";

const LOGO_URL = "/criye-logo.png";

function ThemeToggle() {
  const [theme, setTheme] = useState(() =>
    typeof document !== "undefined" ? (document.documentElement.dataset.theme || "dark") : "dark"
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("criye_theme", theme); } catch (_e) { /* ignore */ }
  }, [theme]);
  const toggle = () => setTheme((t) => (t === "light" ? "dark" : "light"));
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-btn"
      className="w-8 h-8 rounded-md flex items-center justify-center border border-white/10 hover:bg-white/5 transition"
      title={theme === "light" ? "Modo oscuro" : "Modo claro"}
      aria-label="Cambiar tema"
    >
      {theme === "light"
        ? <Moon size={14} weight="duotone" className="text-turquoise" />
        : <Sun size={14} weight="duotone" className="text-amber-300" />}
    </button>
  );
}

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin", "vendedor"] },
  { to: "/pos", label: "Punto de Venta", icon: ShoppingCart, roles: ["admin", "vendedor"] },
  { to: "/inventario", label: "Inventario", icon: Package, roles: ["admin", "vendedor"] },
  { to: "/reparaciones", label: "Reparaciones", icon: Wrench, roles: ["admin", "vendedor"] },
  { to: "/cambio-turno", label: "Cambio de Turno", icon: ArrowsLeftRight, roles: ["admin", "vendedor"] },
  { to: "/clientes", label: "Clientes", icon: Users, roles: ["admin", "vendedor"] },
  { to: "/proveedores", label: "Proveedores", icon: Truck, roles: ["admin"] },
  { to: "/billeteras", label: "Billeteras", icon: Wallet, roles: ["admin", "vendedor"] },
  { to: "/ventas", label: "Historial Ventas", icon: Receipt, roles: ["admin", "vendedor"] },
  { to: "/usuarios", label: "Usuarios", icon: UserGear, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleItems = NAV_ITEMS.filter((it) => it.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-background text-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 fixed left-0 top-0 bottom-0 flex-col border-r border-white/5 bg-[#0A0A0A] z-30">
        <div className="px-6 py-6 border-b border-white/5">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-3 group"
            data-testid="sidebar-logo"
          >
            <img src={LOGO_URL} alt="CRIYE" className="w-10 h-10 transition-transform group-hover:scale-110" />
            <div className="text-left">
              <div className="text-lg font-heading font-bold text-brand-gradient">CRIYE</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">Control Panel</div>
            </div>
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 px-3 mb-2 font-mono">Menu</p>
          {visibleItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to.replace('/','')}`}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                <Icon size={18} weight="regular" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-white/10" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-brand-gradient flex items-center justify-center text-black font-bold text-sm">
                {user?.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-turquoise font-mono">{user?.role}</div>
            </div>
            <ThemeToggle />
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-white border border-white/5 rounded-md hover:bg-white/5 transition"
          >
            <SignOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="CRIYE" className="w-8 h-8" />
          <span className="font-heading font-bold text-brand-gradient">CRIYE</span>
        </div>
        <button onClick={logout} className="text-zinc-400" data-testid="mobile-logout-btn">
          <SignOut size={20} />
        </button>
      </header>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/5 grid grid-cols-5 gap-1 px-2 py-2">
        {visibleItems.slice(0, 5).map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2 rounded ${isActive ? "text-turquoise" : "text-zinc-500"}`}
            >
              <Icon size={20} />
              <span className="text-[10px]">{it.label.split(' ')[0]}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Main content */}
      <main className="flex-1 md:ml-64 pt-16 md:pt-0 pb-20 md:pb-0">
        {children}
      </main>
    </div>
  );
}
